import { useState } from 'react';
import { useForm, useWatch, type Control, type Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  FormActions,
  FormLayout,
  FormSection,
  MoneyInput,
  TagInput,
} from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import {
  PatchAdminV1OrgStoresIdBodyWeightUnit,
  type Store,
} from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { MediaIdField } from '@/features/media';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { changesDefaultCurrency, fromStore, settingsField, toStoreUpdate } from '../api/mappers';
import { useUpdateStore } from '../hooks/use-store-mutations';
import {
  orderNumberExample,
  storeSettingsSchema,
  type StoreSettingsInput,
  type StoreSettingsOutput,
} from '../schemas/store-form-schema';
import { storeSaveErrorTarget } from '../schemas/store-rules';

import { CurrencySelect } from './currency-select';
import { TimezoneSelect } from './timezone-select';

type TextPath =
  | 'name'
  | 'slug'
  | 'legalName'
  | 'contactEmail'
  | 'supportEmail'
  | 'supportPhone'
  | 'primaryDomain'
  | 'orderNumberPrefix'
  | 'defaultLocale'
  | 'countryCode'
  | 'address.line1'
  | 'address.line2'
  | 'address.city'
  | 'address.region'
  | 'address.postalCode'
  | 'address.countryCode'
  | 'checkout.termsUrl'
  | 'checkout.customerLinkUrlTemplate'
  | 'tracking.ga4Id'
  | 'tracking.metaPixelId'
  | 'tracking.tiktokPixelId'
  | 'tracking.pinterestTagId'
  | 'tracking.googleAdsId'
  | 'seo.titleTemplate'
  | 'seo.robots'
  | 'seo.organizationLegalName'
  | 'seo.twitterHandle';

/** Re-renders on its own as the three number fields change, not the whole form. */
function OrderNumberExample({
  control,
  fallback,
}: {
  control: Control<StoreSettingsInput, unknown, StoreSettingsOutput>;
  fallback: number;
}) {
  const { t } = useTranslation('stores');
  const [prefix, next, padding] = useWatch({
    control,
    name: ['orderNumberPrefix', 'nextOrderNumber', 'orderNumberPadding'],
  });
  const number = Number(next);
  const digits = Number(padding);
  const example = orderNumberExample(
    prefix.trim(),
    Number.isInteger(number) && number > 0 ? number : fallback,
    Number.isInteger(digits) && digits >= 0 && digits <= 8 ? digits : 0,
  );
  return (
    <p className="text-muted-foreground text-sm" data-testid="order-number-example">
      {t('fields.orderNumberExample', { example })}
    </p>
  );
}

export interface StoreSettingsFormProps {
  store: Store;
  /** Fields disabled and no Save: the person may look but the backend would refuse a change. */
  readOnly?: boolean;
}

/**
 * Profile, address, order numbers, currencies and locales, checkout, tracking and SEO settings
 * of one store. Sections sit two abreast on wide screens so the form uses the page width.
 */
export function StoreSettingsForm({ store, readOnly = false }: StoreSettingsFormProps) {
  const { t } = useTranslation(['stores', 'common']);
  const update = useUpdateStore(store.id);
  const message = useErrorMessage();
  const form = useForm<StoreSettingsInput, unknown, StoreSettingsOutput>({
    resolver: useI18nResolver(storeSettingsSchema),
    defaultValues: fromStore(store),
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError, settingsField);
  const supportedCurrencies = useWatch({ control: form.control, name: 'supportedCurrencies' });
  const [pendingCurrency, setPendingCurrency] = useState<StoreSettingsOutput | null>(null);
  const lockedCurrency = store.currencyLocked;

  const save = async (values: StoreSettingsOutput) => {
    form.clearErrors('root.server');
    try {
      const saved = await update.mutateAsync(toStoreUpdate(values));
      form.reset(fromStore(saved));
    } catch (error) {
      const target = storeSaveErrorTarget(error);
      if (target) {
        form.setError(target.field, { type: 'server', message: t(target.key, target.values) });
        return;
      }
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  };

  const submit = form.handleSubmit(async (values) => {
    if (changesDefaultCurrency(store, values)) {
      setPendingCurrency(values);
      return;
    }
    await save(values);
  });

  useSaveBar({ form, onSave: submit, saving: update.isPending });

  const text = (
    name: TextPath,
    options: {
      hint?: string;
      mono?: boolean;
      upper?: boolean;
      placeholder?: string;
      anchor?: string;
    } = {},
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem id={options.anchor}>
          <FormLabel>{t(`stores:fields.${name}`)}</FormLabel>
          <FormControl>
            <Input
              {...field}
              value={typeof field.value === 'string' ? field.value : ''}
              placeholder={options.placeholder}
              className={
                options.mono ? 'font-mono text-[13px]' : options.upper ? 'uppercase' : undefined
              }
            />
          </FormControl>
          {options.hint ? <FormDescription>{options.hint}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const list = (name: 'supportedCurrencies' | 'supportedLocales', hint: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`stores:fields.${name}`)}</FormLabel>
          <FormControl>
            <TagInput
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              max={20}
              maxLength={5}
              disabled={readOnly}
              removeLabel={(tag) => t('stores:fields.remove', { value: tag })}
            />
          </FormControl>
          <FormDescription>{hint}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const toggle = (name: Path<StoreSettingsInput> & `checkout.${string}`, label: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-center gap-2.5">
          <FormControl>
            <Checkbox
              checked={field.value === true}
              onCheckedChange={(v) => field.onChange(v === true)}
            />
          </FormControl>
          <FormLabel className="font-normal">{label}</FormLabel>
        </FormItem>
      )}
    />
  );

  const number = (
    name: 'orderNumberPadding' | 'nextOrderNumber',
    options: { min: number; max?: number; hint: string },
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`stores:fields.${name}`)}</FormLabel>
          <FormControl>
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              min={options.min}
              max={options.max}
              className="font-mono text-[13px] tabular-nums"
              {...field}
              value={field.value}
            />
          </FormControl>
          <FormDescription>{options.hint}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const switchField = (
    name: 'seo.indexable' | 'crawlers.allowTraining' | 'documents.showPrices',
    label: string,
    hint: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-row items-start gap-3">
          <FormControl>
            <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
          </FormControl>
          <div className="grid gap-1">
            <FormLabel>{label}</FormLabel>
            <FormDescription>{hint}</FormDescription>
          </div>
        </FormItem>
      )}
    />
  );

  const rootError = form.formState.errors.root?.server?.message;

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate>
        {/* A disabled fieldset reaches every native input and Radix trigger below it at once. */}
        <fieldset disabled={readOnly} className="contents min-w-0">
          <FormLayout>
            {rootError ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{rootError}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
              <FormSection
                id={SECTION_ANCHORS.storeProfile}
                tour="settings.store-details.profile"
                title={t('stores:sections.profile')}
                description={t('stores:sections.profileDescription')}
              >
                <div className="grid gap-4 md:grid-cols-2">
                  {text('name')}
                  {text('slug', { mono: true, hint: t('stores:fields.slugHint') })}
                  {text('legalName')}
                  {text('primaryDomain', {
                    mono: true,
                    hint: t('stores:fields.primaryDomainHint'),
                    anchor: SECTION_ANCHORS.primaryDomain,
                    placeholder: 'shop.example.com',
                  })}
                  {text('contactEmail')}
                  {text('supportEmail')}
                  {text('supportPhone')}
                </div>
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.businessAddress}
                tour="settings.store-details.address"
                title={t('stores:sections.address')}
                description={t('stores:sections.addressDescription')}
              >
                {text('address.line1')}
                {text('address.line2')}
                <div className="grid gap-4 md:grid-cols-2">
                  {text('address.city')}
                  {text('address.region')}
                  {text('address.postalCode')}
                  {text('address.countryCode', {
                    upper: true,
                    hint: t('stores:fields.address.countryCodeHint'),
                  })}
                </div>
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.orderNumbers}
                tour="settings.store-details.order-numbers"
                title={t('stores:sections.orderNumbers')}
                description={t('stores:sections.orderNumbersDescription')}
              >
                <div className="grid gap-4 md:grid-cols-2">
                  {text('orderNumberPrefix', {
                    mono: true,
                    hint: t('stores:fields.orderNumberPrefixHint'),
                  })}
                  {number('orderNumberPadding', {
                    min: 0,
                    max: 8,
                    hint: t('stores:fields.orderNumberPaddingHint'),
                  })}
                  {number('nextOrderNumber', {
                    min: store.nextOrderNumber,
                    hint: t('stores:fields.nextOrderNumberHint', {
                      current: store.nextOrderNumber,
                    }),
                  })}
                </div>
                <OrderNumberExample control={form.control} fallback={store.nextOrderNumber} />
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.currencies}
                tour="settings.store-details.currencies"
                title={t('stores:sections.locale')}
                description={t('stores:sections.localeDescription')}
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="defaultCurrency"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t('stores:fields.defaultCurrency')}</FormLabel>
                        <FormControl>
                          <CurrencySelect
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            preferred={supportedCurrencies}
                            disabled={readOnly || lockedCurrency}
                            aria-invalid={fieldState.invalid}
                          />
                        </FormControl>
                        <FormDescription>
                          {lockedCurrency
                            ? t('stores:fields.defaultCurrencyLocked')
                            : t('stores:fields.defaultCurrencyHint')}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {list('supportedCurrencies', t('stores:fields.supportedCurrenciesHint'))}
                  {text('defaultLocale')}
                  {list('supportedLocales', t('stores:fields.supportedLocalesHint'))}
                  <FormField
                    control={form.control}
                    name="timezone"
                    render={({ field, fieldState }) => (
                      <FormItem id={SECTION_ANCHORS.timeZone}>
                        <FormLabel>{t('stores:fields.timezone')}</FormLabel>
                        <FormControl>
                          <TimezoneSelect
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={readOnly}
                            aria-invalid={fieldState.invalid}
                          />
                        </FormControl>
                        <FormDescription>{t('stores:fields.timezoneHint')}</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {text('countryCode', { upper: true })}
                  <FormField
                    control={form.control}
                    name="weightUnit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('stores:fields.weightUnit')}</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={readOnly}
                        >
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(PatchAdminV1OrgStoresIdBodyWeightUnit).map((unit) => (
                              <SelectItem key={unit} value={unit}>
                                {t(`stores:weightUnit.${unit}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.checkout}
                tour="settings.store-details.checkout"
                title={t('stores:sections.checkout')}
                description={t('stores:sections.checkoutDescription')}
              >
                {toggle('checkout.guestCheckout', t('stores:fields.checkout.guestCheckout'))}
                {toggle('checkout.requirePhone', t('stores:fields.checkout.requirePhone'))}
                {toggle(
                  'checkout.requireTermsAcceptance',
                  t('stores:fields.checkout.requireTermsAcceptance'),
                )}
                {text('checkout.customerLinkUrlTemplate', {
                  mono: true,
                  placeholder: 'https://shop.example.com/{path}',
                  hint: t('stores:fields.checkout.customerLinkUrlTemplateHint'),
                })}
                {text('checkout.termsUrl', {
                  hint: t('stores:fields.checkout.termsUrlHint'),
                  placeholder: 'https://',
                })}
                {toggle('checkout.codEnabled', t('stores:fields.checkout.codEnabled'))}
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="checkout.codMaxAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('stores:fields.checkout.codMaxAmount')}</FormLabel>
                        <FormControl>
                          <MoneyInput
                            currency={store.defaultCurrency}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormDescription>
                          {t('stores:fields.checkout.codMaxAmountHint')}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="checkout.codFeeAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('stores:fields.checkout.codFeeAmount')}</FormLabel>
                        <FormControl>
                          <MoneyInput
                            currency={store.defaultCurrency}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.tracking}
                title={t('stores:sections.tracking')}
                description={t('stores:sections.trackingDescription')}
              >
                <div className="grid gap-4 md:grid-cols-2">
                  {text('tracking.ga4Id', { mono: true })}
                  {text('tracking.metaPixelId', { mono: true })}
                  {text('tracking.tiktokPixelId', { mono: true })}
                  {text('tracking.pinterestTagId', { mono: true })}
                  {text('tracking.googleAdsId', { mono: true })}
                </div>
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.seoDefaults}
                tour="settings.store-details.seo"
                title={t('stores:sections.seo')}
                description={t('stores:sections.seoDescription')}
              >
                {text('seo.titleTemplate', { hint: t('stores:fields.seo.titleTemplateHint') })}
                <FormField
                  control={form.control}
                  name="seo.defaultMetaDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('stores:fields.seo.defaultMetaDescription')}</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {text('seo.robots', { mono: true, hint: t('stores:fields.seo.robotsHint') })}
                {switchField(
                  'seo.indexable',
                  t('stores:fields.seo.indexable'),
                  t('stores:fields.seo.indexableHint'),
                )}
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.brandInSearch}
                title={t('stores:sections.organization')}
                description={t('stores:sections.organizationDescription')}
              >
                <div className="grid gap-4 md:grid-cols-2">
                  {text('seo.organizationLegalName', {
                    hint: t('stores:fields.seo.organizationLegalNameHint'),
                  })}
                  {text('seo.twitterHandle', { hint: t('stores:fields.seo.twitterHandleHint') })}
                </div>
                <FormField
                  control={form.control}
                  name="seo.socialProfiles"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('stores:fields.seo.socialProfiles')}</FormLabel>
                      <FormControl>
                        <TagInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          max={20}
                          maxLength={500}
                          disabled={readOnly}
                          placeholder="https://instagram.com/…"
                          removeLabel={(url) => t('stores:fields.remove', { value: url })}
                        />
                      </FormControl>
                      <FormDescription>{t('stores:fields.seo.socialProfilesHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="seo.logoMediaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('stores:fields.seo.logo')}</FormLabel>
                      <MediaIdField
                        value={field.value}
                        onChange={(id) => field.onChange(id)}
                        label={t('stores:fields.seo.logoLabel')}
                      />
                      <FormDescription>{t('stores:fields.seo.logoHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.aiCrawlers}
                title={t('stores:sections.crawlers')}
                description={t('stores:sections.crawlersDescription')}
              >
                {switchField(
                  'crawlers.allowTraining',
                  t('stores:fields.crawlers.allowTraining'),
                  t('stores:fields.crawlers.allowTrainingHint'),
                )}
              </FormSection>
              <FormSection
                id={SECTION_ANCHORS.documents}
                title={t('stores:sections.documents')}
                description={t('stores:sections.documentsDescription')}
              >
                <FormField
                  control={form.control}
                  name="documents.footer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('stores:fields.documents.footer')}</FormLabel>
                      <FormControl>
                        <Textarea rows={4} maxLength={2000} {...field} />
                      </FormControl>
                      <FormDescription>{t('stores:fields.documents.footerHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {switchField(
                  'documents.showPrices',
                  t('stores:fields.documents.showPrices'),
                  t('stores:fields.documents.showPricesHint'),
                )}
              </FormSection>
            </div>
            {readOnly ? null : (
              <FormActions
                isSubmitting={update.isPending}
                isDirty={form.formState.isDirty}
                tour="settings.store-details.save"
              />
            )}
          </FormLayout>
        </fieldset>
      </form>
      <AlertDialog
        open={pendingCurrency !== null}
        onOpenChange={(open) => {
          if (!open) setPendingCurrency(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('stores:currency.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('stores:currency.confirmDescription', {
                from: store.defaultCurrency,
                to: pendingCurrency?.defaultCurrency ?? '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const values = pendingCurrency;
                setPendingCurrency(null);
                if (values) void save(values);
              }}
            >
              {t('stores:currency.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}
